import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Hero from './Hero';
import sectionsData from '../data/sections.json';

interface Job {
  id: string;
  title: string;
  company: string;
  period: string;
  location: string;
  shortDescription?: string;
  details?: {
    responsibilities?: string[];
    technologies?: string[];
    achievements?: string[];
  };
  links?: Array<{
    text: string;
    url: string;
  }>;
}

interface WorkExperienceProps {
  data?: {
    title: string;
    subtitle: string;
    description: string;
    background?: string;
    metadata?: {
      experience?: Job[];
    };
  };
}

const WorkExperience: React.FC<WorkExperienceProps> = ({ data }) => {
  const [experiences, setExperiences] = useState<Job[]>([]);
  const [activeJob, setActiveJob] = useState<string | null>(null);
  
  useEffect(() => {
    // If data is provided via props, use it
    if (data?.metadata?.experience) {
      setExperiences(data.metadata.experience);
      // Set the first job as active by default
      if (data.metadata.experience.length > 0) {
        setActiveJob(data.metadata.experience[0].id);
      }
    } else {
      // Otherwise, use the imported sections.json data
      try {
        const workSection = sectionsData.sections.find((section: any) => section.id === 'work');
        console.log('Work section data:', workSection);
        
        if (workSection?.metadata?.experience) {
          setExperiences(workSection.metadata.experience);
          // Set the first job as active by default
          if (workSection.metadata.experience.length > 0) {
            setActiveJob(workSection.metadata.experience[0].id);
          }
        } else {
          // Fallback to default data if no data is available
          const defaultJob = {
            id: '1',
            company: "No data available",
            title: "Please check data source",
            period: "N/A",
            location: "N/A",
            details: {
              technologies: ["React", "TypeScript"],
              achievements: ["Please check your data source"]
            }
          };
          setExperiences([defaultJob]);
          setActiveJob('1');
        }
      } catch (error) {
        console.error("Failed to load work experience data:", error);
        // Set default data on error
        const errorJob = {
          id: '1',
          company: "Error loading data",
          title: "Check console for details",
          period: "N/A",
          location: "N/A",
          details: {
            technologies: ["React", "TypeScript"],
            achievements: ["Please check your data source"]
          }
        };
        setExperiences([errorJob]);
        setActiveJob('1');
      }
    }
  }, [data]);

  // Find the active job
  const activeJobData = experiences.find(job => job.id === activeJob);

  // Log the data being passed to Hero
  useEffect(() => {
    console.log('WorkExperience passing to Hero:', {
      title: data?.title || "Work Experience",
      description: data?.description || "Professional journey and technical expertise",
      background: data?.background || 'cody-smug'
    });
  }, [data]);

  return (
    <div className="min-h-screen">
      <Hero 
        data={{
          title: data?.title || "Work Experience",
          description: data?.description || "Professional journey and technical expertise",
          background: data?.background || 'cody-smug'
        }}
      />

      <div className="py-6 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 mb-8"
          >
            {/* Timeline Navigation */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">Career Timeline</h3>
              <div className="flex flex-nowrap overflow-x-auto pb-2 space-x-2 md:space-x-4">
                {experiences.map((job) => (
                  <button
                    key={job.id}
                    onClick={() => setActiveJob(job.id)}
                    className={`
                      flex-shrink-0 px-4 py-2 rounded-full transition-all
                      ${activeJob === job.id 
                        ? 'bg-white/10 text-white border border-white/20' 
                        : 'bg-black/40 text-gray-300 border border-gray-800 hover:bg-black/60'}
                    `}
                  >
                    <span className="whitespace-nowrap">{job.period}</span>
                    <span className="block text-xs opacity-75 whitespace-nowrap">{job.company}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Active Job Details */}
            {activeJobData && (
              <motion.div
                key={activeJobData.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                {/* Header */}
                <div className="border-b border-gray-800 pb-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2 className="text-3xl font-bold text-white mb-1">{activeJobData.title}</h2>
                      <p className="text-xl text-gray-400">{activeJobData.company}</p>
                    </div>
                    <div className="mt-2 md:mt-0 md:text-right">
                      <p className="text-gray-300">{activeJobData.period}</p>
                      <p className="text-gray-400">{activeJobData.location}</p>
                    </div>
                  </div>
                  {activeJobData.shortDescription && (
                    <p className="text-gray-200 mt-4 text-lg">{activeJobData.shortDescription}</p>
                  )}
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {activeJobData.details?.responsibilities && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <h4 className="text-xl text-white font-medium mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Responsibilities
                        </h4>
                        <ul className="space-y-2">
                          {activeJobData.details.responsibilities.map((responsibility, i) => (
                            <motion.li 
                              key={i} 
                              className="flex items-start gap-2 text-gray-300"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 + (i * 0.1) }}
                            >
                              <span className="text-gray-500 mt-1">•</span>
                              <span>{responsibility}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    )}

                    {activeJobData.details?.achievements && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <h4 className="text-xl text-white font-medium mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                          Key Achievements
                        </h4>
                        <ul className="space-y-2">
                          {activeJobData.details.achievements.map((achievement, i) => (
                            <motion.li 
                              key={i} 
                              className="flex items-start gap-2 text-gray-300"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 + (i * 0.1) }}
                            >
                              <span className="text-gray-500 mt-1">•</span>
                              <span>{achievement}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {activeJobData.details?.technologies && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <h4 className="text-xl text-white font-medium mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          Technologies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {activeJobData.details.technologies.map((tech, i) => (
                            <motion.span
                              key={tech}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.3 + (i * 0.05) }}
                              className="px-3 py-1 bg-black/50 rounded-full text-sm text-gray-200 border border-gray-800"
                            >
                              {tech}
                            </motion.span>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {activeJobData.links && activeJobData.links.length > 0 && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="mt-6"
                      >
                        <h4 className="text-xl text-white font-medium mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Related Links
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {activeJobData.links.map((link, i) => (
                            <motion.a 
                              key={i}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 + (i * 0.1) }}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-md transition-colors border border-gray-800"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              {link.text}
                            </motion.a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WorkExperience; 